/**
 * How one gauge is drawn, and where that choice is kept.
 *
 * Health, stamina, mana and faith are the same gauge measuring different things,
 * so how they look is one set of choices rather than four. Where somebody wants
 * them is already theirs; this is the rest of it.
 *
 * Kept per gauge rather than shared, because the reasons differ: a health bar
 * across the bottom of the screen and a small mana ring beside the buttons is a
 * perfectly ordinary thing to want.
 */

/** A ring, a bar lying down, or a bar standing up. */
export type MeterShape = 'circle' | 'horizontal' | 'vertical';

/** What the number beside it says. */
export type MeterValue = 'current' | 'both' | 'percent';

/**
 * Where the icon sits, for the two bar shapes.
 *
 * First and second rather than left and right, because the same choice reads as
 * top and bottom once the bar is stood on its end, and one word for one idea is
 * better than two words that have to be kept in step.
 */
export type MeterIcon = 'first' | 'second' | 'hidden';

export interface MeterLook {
  shape: MeterShape;
  value: MeterValue;
  icon: MeterIcon;
  /** Circle only: the number in the middle, large, instead of the icon. */
  bigText: boolean;
}

export const DEFAULT_LOOK: MeterLook = {
  shape: 'circle',
  value: 'current',
  icon: 'first',
  bigText: false,
};

const STORE = 'inverse.meter.';

export function loadLook(kind: string): MeterLook {
  try {
    const raw = window.localStorage.getItem(STORE + kind);
    if (!raw) return { ...DEFAULT_LOOK };

    const held = JSON.parse(raw) as Partial<MeterLook>;
    return {
      shape: held.shape === 'horizontal' || held.shape === 'vertical' ? held.shape : 'circle',
      value: held.value === 'both' || held.value === 'percent' ? held.value : 'current',
      icon: held.icon === 'second' || held.icon === 'hidden' ? held.icon : 'first',
      bigText: held.bigText === true,
    };
  } catch {
    // A browser that refuses storage is a browser somebody is still playing in.
    return { ...DEFAULT_LOOK };
  }
}

export function saveLook(kind: string, look: MeterLook): void {
  try {
    window.localStorage.setItem(STORE + kind, JSON.stringify(look));
  } catch {
    // Nothing to do about it, and nothing worth stopping for.
  }
}

/** What the gauge says beside itself. */
export function readingOf(look: MeterLook, current: number, max: number): string {
  if (look.value === 'both') return `${current}/${max}`;
  if (look.value === 'percent') {
    return `${max > 0 ? Math.round((current / max) * 100) : 0}%`;
  }
  return String(current);
}
